import { Box, Button, Grid } from '@mui/material';
import DatePickerField from '@acorn-portal/shared/src/components/datePicker/DropDownDatePicker';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import BasicStepper from '@acorn-portal/shared/src/components/stepper/BasicStepper';
import { Form, Formik } from 'formik';
import { FC, useState } from 'react';
import { Realms } from '@acorn-portal/shared/src/services/authenticationApi';
import { minTwoDigits } from '@acorn-portal/shared/src/utils';
import { REGISTRATION_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import {
	NAME_REGEX,
	PASSWORD_REGEX,
	POLICY_NUMBER_REGEX,
	POSTALCODE_REGEX,
	VEHICLE_REGISTRATION_REGEX,
} from '@acorn-portal/shared/src/utils/constants/Regex';
import IDate from '@acorn-portal/shared/src/utils/types/date';
import is17OrOver from '@acorn-portal/shared/src/utils/isSeventteenOrOver';
import * as Yup from 'yup';
import GetRealm from '@acorn-portal/shared/src/utils/getRealm';
import PasswordRequirements from '../copy/PasswordRequirements';
import {
	REGISTRATION_POLICY_NUMBER_MODAL,
	REGISTRATION_POST_CODE_MODAL,
	REGISTRATION_VEHICLE_REGISTRATION_MODAL,
} from '@acorn-portal/shared/src/utils/modalText';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export interface IRegistrationFormProps {
	onSubmit: (form: IRegisterForm) => void;
	email: string;
}
export interface IRegisterForm {
	firstName: string;
	lastName: string;
	email: string;
	password: string;
	confirmPassword: string;
	policyNumber: string;
	dateOfBirth: IDate;
	postCode: string;
	vehicleRegistrationNumber: string;
	realm: Realms;
}

interface DateOfBirth {
	year?: string;
	month?: string;
	day?: string;
}

const getFormDate = (value?: Date | null): IDate => {
	if (!value || value === null) {
		return { year: '', month: '', day: '' };
	}
	const tempDate = new Date(value as unknown as string);
	const year = tempDate.getFullYear();
	const month = tempDate.getMonth();
	const day = tempDate.getDate();
	return {
		year: year.toString(),
		month: minTwoDigits(month + 1),
		day: minTwoDigits(day),
	};
};

const step1Schema = Yup.object().shape({
	firstName: Yup.string().matches(NAME_REGEX, REGISTRATION_ERRORS.firstName).required(REGISTRATION_ERRORS.firstName),
	lastName: Yup.string().matches(NAME_REGEX, REGISTRATION_ERRORS.lastName).required(REGISTRATION_ERRORS.lastName),
	dateOfBirth: Yup.object()
		.shape({
			year: Yup.string().required(REGISTRATION_ERRORS.dateOfBirth),
			month: Yup.string().required(REGISTRATION_ERRORS.dateOfBirth),
			day: Yup.string().required(REGISTRATION_ERRORS.dateOfBirth),
		})
		.test('valid-date', 'You must be at least 17 years old to use this service', (value: DateOfBirth | undefined) => {
			if (!value || !value.year || !value.month || !value.day) {
				return false;
			}
			const { year, month, day } = value;
			const dateOfBirth = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));

			return is17OrOver(dateOfBirth);
		}),
});

const step2Schema = Yup.object().shape({
	postCode: Yup.string().matches(POSTALCODE_REGEX, REGISTRATION_ERRORS.postCode).required(REGISTRATION_ERRORS.postCode),
	policyNumber: Yup.string()
		.matches(POLICY_NUMBER_REGEX, REGISTRATION_ERRORS.policyNumberValid)
		.required(REGISTRATION_ERRORS.policyNumber)
		.min(7),
	vehicleRegistrationNumber: Yup.string()
		.matches(VEHICLE_REGISTRATION_REGEX, REGISTRATION_ERRORS.vehicleRegistrationNumber)
		.required(REGISTRATION_ERRORS.vehicleRegistrationNumber),
});

const step3Schema = Yup.object().shape({
	email: Yup.string().email(REGISTRATION_ERRORS.emailValidation).required(REGISTRATION_ERRORS.email),
	password: Yup.string()
		.required(REGISTRATION_ERRORS.password)
		.matches(PASSWORD_REGEX, REGISTRATION_ERRORS.passwordComplexity),
	confirmPassword: Yup.string()
		.required(REGISTRATION_ERRORS.confirmPassword)
		.test('password-confirmation', REGISTRATION_ERRORS.passwordMismatch, (value, form) => {
			if (form.parent) {
				return value === form.parent?.password;
			}
			return false;
		}),
});

const RegistrationForm: FC<IRegistrationFormProps> = ({ onSubmit, email }) => {
	const [step, setStep] = useState(0);

	const initialValues: IRegisterForm = {
		firstName: '',
		lastName: '',
		email,
		password: '',
		confirmPassword: '',
		policyNumber: '',
		dateOfBirth: getFormDate(null),
		postCode: '',
		vehicleRegistrationNumber: '',
		realm: GetRealm(),
	};

	const schemaArray = [step1Schema, step2Schema, step3Schema];
	const handleOnSubmit = async (form: IRegisterForm) => {
		if (step + 1 < schemaArray.length) {
			setStep(step + 1);
			return;
		}

		onSubmit(form);
	};
	return (
		<Box sx={{ mt: 2, pb: 2, m: 2 }}>
			<Box sx={{ textAlign: 'left' }}>
				<Formik<IRegisterForm>
					initialValues={initialValues}
					onSubmit={handleOnSubmit}
					validationSchema={schemaArray[step]}
					validateOnBlur={false}
				>
					{(formik) => (
						<Form>
							<FormikLogErrors />
							<BasicStepper
								onStepChange={setStep}
								stepOverride={step}
								steps={[
									{
										stepName: 'Your Details',
										stepDetails: (
											<Grid container>
												<Grid size={{ xs: 12, md: 12 }}>
													<FormTextField
														id='firstName'
														name='firstName'
														label='First Name'
														autoComplete='given-name'
														error={formik.touched.firstName && Boolean(formik.errors.firstName)}
														helperText={formik.touched.firstName && formik.errors.firstName}
														onChange={formik.handleChange}
														value={formik.values.firstName}
														onBlur={formik.handleBlur}
													/>
												</Grid>
												<Grid size={{ xs: 12, md: 12 }}>
													<FormTextField
														id='lastName'
														name='lastName'
														label='Last Name'
														autoComplete='family-name'
														error={formik.touched.lastName && Boolean(formik.errors.lastName)}
														helperText={formik.touched.lastName && formik.errors.lastName}
														onChange={formik.handleChange}
														value={formik.values.lastName}
														onBlur={formik.handleBlur}
													/>
												</Grid>
												<Grid size={{ xs: 12, md: 12 }}>
													<DatePickerField
														id='dateOfBirth'
														onChange={(value) => formik.setFieldValue('dateOfBirth', value)}
														label='Date Of Birth'
														value={formik.values.dateOfBirth}
														error={
															(formik.touched.dateOfBirth?.day && Boolean(formik.errors.dateOfBirth?.day)) ||
															(formik.touched.dateOfBirth?.month && Boolean(formik.errors.dateOfBirth?.month)) ||
															(formik.touched.dateOfBirth?.year && Boolean(formik.errors.dateOfBirth?.year)) ||
															(formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth))
														}
														helperText={
															(formik.touched.dateOfBirth?.day && formik.errors.dateOfBirth?.day) ||
															(formik.touched.dateOfBirth?.month && formik.errors.dateOfBirth?.month) ||
															(formik.touched.dateOfBirth?.year && formik.errors.dateOfBirth?.year) ||
															(formik.touched.dateOfBirth && (formik.errors.dateOfBirth as string))
														}
													/>
												</Grid>
											</Grid>
										),
										nextButton: (
											<Button
												type='submit'
												variant='contained'
												color='secondary'
												size='large'
												endIcon={<ArrowForwardIcon />}
												sx={{
													float: 'right',
													minWidth: '150px',
												}}
											>
												Next
											</Button>
										),
									},
									{
										stepName: 'Policy Details',
										stepDetails: (
											<Grid container>
												<Grid size={{ xs: 12, md: 12 }}>
													<FormTextField
														id='vehicleRegistrationNumber'
														name='vehicleRegistrationNumber'
														label='Vehicle Registration Number'
														error={
															formik.touched.vehicleRegistrationNumber &&
															Boolean(formik.errors.vehicleRegistrationNumber)
														}
														helperText={
															formik.touched.vehicleRegistrationNumber && formik.errors.vehicleRegistrationNumber
														}
														onChange={formik.handleChange}
														value={formik.values.vehicleRegistrationNumber}
														modal={{
															title: REGISTRATION_VEHICLE_REGISTRATION_MODAL.title,
															description: REGISTRATION_VEHICLE_REGISTRATION_MODAL.description,
														}}
														onBlur={formik.handleBlur}
													/>
												</Grid>
												<Grid size={{ xs: 12, md: 12 }}>
													<FormTextField
														id='postCode'
														name='postCode'
														label='Postcode'
														error={formik.touched.postCode && Boolean(formik.errors.postCode)}
														helperText={formik.touched.postCode && formik.errors.postCode}
														onChange={formik.handleChange}
														value={formik.values.postCode}
														autoComplete='postal-code'
														modal={{
															title: REGISTRATION_POST_CODE_MODAL.title,
															description: REGISTRATION_POST_CODE_MODAL.description,
														}}
														onBlur={formik.handleBlur}
													/>
												</Grid>
												<Grid size={{ xs: 12, md: 12 }}>
													<FormTextField
														id='policyNumber'
														name='policyNumber'
														label='Policy Number'
														error={formik.touched.policyNumber && Boolean(formik.errors.policyNumber)}
														helperText={formik.touched.policyNumber && formik.errors.policyNumber}
														onChange={formik.handleChange}
														value={formik.values.policyNumber}
														modal={{
															title: REGISTRATION_POLICY_NUMBER_MODAL.title,
															description: REGISTRATION_POLICY_NUMBER_MODAL.description,
														}}
														onBlur={formik.handleBlur}
													/>
												</Grid>
											</Grid>
										),
										nextButton: (
											<Button
												type='submit'
												variant='contained'
												color='secondary'
												size='large'
												endIcon={<ArrowForwardIcon />}
												sx={{
													float: 'right',
													minWidth: '150px',
												}}
											>
												Next
											</Button>
										),
									},
									{
										stepName: 'Set Password',
										stepDetails: (
											<Grid container>
												<PasswordRequirements password={formik.values.password} />
												<Grid size={{ xs: 12, md: 12 }}>
													<FormTextField
														id='password'
														name='password'
														label='Password'
														format='password'
														error={formik.touched.password && Boolean(formik.errors.password)}
														helperText={formik.touched.password && formik.errors.password}
														onChange={formik.handleChange}
														value={formik.values.password}
														onBlur={formik.handleBlur}
													/>
												</Grid>
												<Grid size={{ xs: 12, md: 12 }}>
													<FormTextField
														id='confirmPassword'
														name='confirmPassword'
														label='Confirm Password'
														format='password'
														error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
														helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
														onChange={formik.handleChange}
														value={formik.values.confirmPassword}
														onBlur={formik.handleBlur}
													/>
												</Grid>
											</Grid>
										),
										nextButton: (
											<Button
												type='submit'
												variant='contained'
												size='large'
												endIcon={<SendIcon />}
												sx={{
													float: 'right',
													minWidth: '150px',
												}}
											>
												Send Email
											</Button>
										),
									},
								]}
							/>
						</Form>
					)}
				</Formik>
			</Box>
		</Box>
	);
};

export default RegistrationForm;
